import { useCallback, useEffect, useRef } from 'react'
import type { FormEvent, RefObject } from 'react'

import { toast } from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import type { SavedReplyItem_SavedReply } from '../../SavedRepliesPopover/SavedReplyItem'
import { useComposerFocusManagement } from '../../../hooks/useComposerFocusManagement'
import {
  shouldSubmitOnKeyPress,
  validateAndTrimReplyText,
} from '../../../Comments/CommentReplyForm/utils/replyFormUtils'
import type { ReplyErrorType } from '../../../types'
import type { InsertedRange } from '../../EngagementInsertedTextHighlight'

import { determineEngagementClickOutsideAction } from './engagementClickOutside'
import { useEngagementReplyDraft } from './useEngagementReplyDraft'
import type {
  EngagementReplyFormChannel,
  EngagementReplyFormStatus,
  EngagementReplySubmitStrategy,
  EngagementReplySubmitStrategyDeps,
} from './types'

/**
 * Delay between submit start and auto-expanding the next repliable engagement.
 * Long enough for the "Sending…" state to register before the card collapses
 * and the next opens. Shared by both reply flows.
 */
const EXPAND_NEXT_DELAY_MS = 800

type UseEngagementReplyFormParams<TReply> = {
  channel: EngagementReplyFormChannel
  textareaRef: RefObject<HTMLTextAreaElement>
  expanded: boolean
  /** Stable identity of the engagement; resets draft highlight + sent replies. */
  resetKey: string
  /** Lazy initial text, evaluated once at mount. */
  getInitialText: () => string
  /** Optional once-pristine late seed (Mentions). */
  lateSeedText?: string
  /** Optional draft persistence (Comments). */
  persistence?: { save: (text: string) => void; clear: () => void }
  /** Fired on every status transition (Comments' `onReplyStatusChange`). */
  onStatusChange?: (status: EngagementReplyFormStatus) => void
  /** Fired once a reply publishes (Mentions' `onReplied`). */
  onPublished?: () => void
  /** Whether to schedule auto-expand-next on submit. */
  shouldExpandNext: boolean
  /** Bound expand-next callback (already carries the id + `onlyRepliable`). */
  onExpandNext?: () => void
  /** Outside-click collapse target + card selectors. */
  clickOutside: {
    onCollapse?: () => void
    /** Resolves the current card, e.g. `[data-comment-id="123"]`. */
    currentCardSelector: string
    /** Matches any engagement card wrapper, e.g. `[data-comment-wrapper="true"]`. */
    cardWrapperSelector: string
  }
  /** Feature-specific submit-strategy hook. */
  useSubmitStrategy: (
    deps: EngagementReplySubmitStrategyDeps<TReply>,
  ) => EngagementReplySubmitStrategy<TReply>
}

/**
 * The shared compose surface. Structurally a superset of
 * `EngagementReplyController` (the seam `EngagementReplyForm` reads) plus the
 * extra fields the feature wrappers expose. Generic over `TReply` so each
 * wrapper surfaces its own optimistic-reply type for `sentReplies`.
 */
export type UseEngagementReplyFormReturn<TReply> = {
  text: string
  status: EngagementReplyFormStatus
  error?: string
  errorType?: ReplyErrorType
  submittingText?: string
  sentReplies: TReply[]
  setText: (text: string) => void
  focus: () => void
  clear: () => void
  submit: (e?: FormEvent) => Promise<void>
  discard: () => void
  insertSuggestion: (text: string) => boolean
  insertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
  insertedRange: InsertedRange | null
  clearInsertedRange: () => void
  savedRepliesRef: RefObject<HTMLDivElement>
  emojiPickerRef: RefObject<HTMLDivElement>
}

/**
 * Composes the shared draft core (`useEngagementReplyDraft`) with an injected,
 * feature-specific submit strategy and **implements `EngagementReplyController`**
 * — the seam the future unified `EngagementCard` reads.
 *
 * Owns the parts that are identical across comments and mentions: the submit
 * skeleton (validate → processing → schedule expand-next → run strategy → map
 * the outcome onto the status machine), the auto-expand-next timer, the
 * `Cmd/Ctrl+Enter` shortcut, focus-on-expand, composer focus restoration, and
 * outside-click collapse. The three divergent bits (mutation, optimistic reply
 * shape, reconciliation) live entirely in the strategy.
 *
 * Adopted by both `useCommentReplyForm` and `useMentionReplyForm`, which pass
 * their feature-specific strategy + options and re-shape the return to their
 * existing public types.
 */
export const useEngagementReplyForm = <TReply>({
  channel,
  textareaRef,
  expanded,
  resetKey,
  getInitialText,
  lateSeedText,
  persistence,
  onStatusChange,
  onPublished,
  shouldExpandNext,
  onExpandNext,
  clickOutside,
  useSubmitStrategy,
}: UseEngagementReplyFormParams<TReply>): UseEngagementReplyFormReturn<TReply> => {
  const draft = useEngagementReplyDraft<TReply>({
    channel,
    textareaRef,
    resetKey,
    getInitialText,
    lateSeedText,
    persistence,
    onStatusChange,
    onPublished,
  })

  const strategy = useSubmitStrategy({
    channel,
    markPublished: draft.markPublished,
    resetKey,
  })

  const { isOpen: isComposerOpen } = usePostComposer()

  // Auto-expand-next timer plumbing (guarded so it never fires after unmount,
  // and cancelled if the reply fails before the delay elapses).
  const isMountedRef = useRef(true)
  const expandNextTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const cancelExpandNext = useCallback(() => {
    if (expandNextTimeoutRef.current) {
      clearTimeout(expandNextTimeoutRef.current)
      expandNextTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
      if (expandNextTimeoutRef.current) {
        clearTimeout(expandNextTimeoutRef.current)
      }
    }
  }, [])

  const { text, status, markProcessing, markPublished, markError, discard } =
    draft
  const { onSubmitStart, submit: runStrategySubmit } = strategy

  const submit = useCallback(
    async (e?: FormEvent): Promise<void> => {
      e?.preventDefault()

      // Block re-entry only while a request is in flight.
      if (status === 'processing') return

      const finalText = validateAndTrimReplyText(text)
      if (!finalText) return

      // Hide the form behind the processing state, then run submit-start side
      // effects (engagement timers, dismissal tracking — comments only).
      markProcessing(finalText)
      onSubmitStart?.(finalText)

      // Collapse this card and open the next repliable one shortly after the
      // "Sending…" state shows — in parallel with the request so it feels
      // responsive. Skipped for resolved cards (re-replies).
      if (shouldExpandNext) {
        cancelExpandNext()
        expandNextTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            onExpandNext?.()
          }
        }, EXPAND_NEXT_DELAY_MS)
      }

      try {
        const outcome = await runStrategySubmit(finalText)

        if (outcome.status === 'published') {
          markPublished(outcome.reply)
        } else if (outcome.status === 'pending') {
          // Request may have been sent despite a transport error — stay in
          // `processing` and let the strategy resolve it out-of-band (Pusher).
        } else {
          cancelExpandNext()
          markError({
            message: outcome.message,
            errorType: outcome.errorType,
            restoreText: finalText,
          })
          if (outcome.toast !== false) {
            toast.error(outcome.message, { duration: 2_000 })
          }
        }
      } catch {
        // The mutation hooks resolve their own errors into outcomes rather than
        // throwing, so reaching here means an unexpected throw in submit-start
        // or strategy wiring. Surface a generic inline error without a toast.
        cancelExpandNext()
        markError({
          message:
            'There was an error sending the reply. Please try again later or contact support.',
          errorType: 'api_error',
          restoreText: finalText,
        })
      }
    },
    [
      status,
      text,
      markProcessing,
      onSubmitStart,
      runStrategySubmit,
      markPublished,
      markError,
      shouldExpandNext,
      onExpandNext,
      cancelExpandNext,
    ],
  )

  // Track text length in a ref so the click-outside listener doesn't
  // re-subscribe on every keystroke.
  const textLengthRef = useRef(0)
  useEffect(() => {
    textLengthRef.current = text.length
  }, [text.length])

  const { onCollapse, currentCardSelector, cardWrapperSelector } = clickOutside

  // Outside-click handling: collapse the expanded card when clicking away.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      // If the composer is open, don't collapse for ANY click — this allows
      // full interaction with the composer and its portaled elements.
      if (isComposerOpen) return

      const currentCardEl = document.querySelector(currentCardSelector)
      const action = determineEngagementClickOutsideAction(event, {
        expanded,
        status,
        currentCardElement:
          currentCardEl instanceof HTMLElement ? currentCardEl : null,
        cardWrapperSelector,
        savedRepliesRef: draft.savedRepliesRef.current,
        emojiPickerRef: draft.emojiPickerRef.current,
      })

      switch (action.type) {
        case 'collapse':
          onCollapse?.()
          break
        case 'discard':
          discard()
          break
        case 'discard-and-collapse':
          discard()
          onCollapse?.()
          break
        case 'none':
          break
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void =>
      document.removeEventListener('mousedown', handleClickOutside)
  }, [
    expanded,
    status,
    discard,
    onCollapse,
    currentCardSelector,
    cardWrapperSelector,
    isComposerOpen,
    draft.savedRepliesRef,
    draft.emojiPickerRef,
  ])

  // Cmd/Ctrl+Enter submits while expanded (Enter alone newlines in the textarea).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!expanded || !textareaRef.current) return
      if (shouldSubmitOnKeyPress(event, expanded)) {
        event.preventDefault()
        void submit()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [expanded, textareaRef, submit])

  // Focus the textarea when the form expands, cursor at the end.
  useEffect(() => {
    if (!expanded) return
    requestAnimationFrame(() => {
      const textarea = textareaRef.current
      if (!textarea) return
      textarea.focus()
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    })
  }, [expanded, textareaRef])

  // Restore focus to the textarea when the composer closes (opened from the
  // reply form's "Start a Post").
  useComposerFocusManagement({
    expanded,
    textareaRef,
    savedRepliesRef: draft.savedRepliesRef,
    emojiPickerRef: draft.emojiPickerRef,
  })

  return {
    text: draft.text,
    status: draft.status,
    error: draft.error,
    errorType: draft.errorType,
    submittingText: draft.submittingText,
    sentReplies: draft.sentReplies,
    setText: draft.setText,
    focus: draft.focus,
    clear: draft.clear,
    submit,
    discard: draft.discard,
    insertSuggestion: draft.insertSuggestion,
    insertSavedReply: draft.insertSavedReply,
    insertedRange: draft.insertedRange,
    clearInsertedRange: draft.clearInsertedRange,
    savedRepliesRef: draft.savedRepliesRef,
    emojiPickerRef: draft.emojiPickerRef,
  }
}
